export type WelcomeMode = "full" | "short" | "reduced";

export interface WelcomeStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const WELCOME_DAY_KEY = "banculem-welcome-day";
export const FULL_WELCOME_DURATION_MS = 6000;
export const SHORT_WELCOME_DURATION_MS = 1550;
export const REDUCED_WELCOME_DURATION_MS = 900;
const LAUNCH_COVER_SELECTOR = "#banculem-launch-cover";

export function welcomeDayKey(now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function resolveWelcomeMode(
  storage: WelcomeStorage | null,
  now = new Date(),
  reduceMotion = false,
): WelcomeMode {
  const today = welcomeDayKey(now);
  let previous = "";
  try {
    previous = storage?.getItem(WELCOME_DAY_KEY) ?? "";
    if (previous !== today) storage?.setItem(WELCOME_DAY_KEY, today);
  } catch {
    previous = "";
  }
  if (reduceMotion) return "reduced";
  return previous === today ? "short" : "full";
}

export function welcomeDurationMs(mode: WelcomeMode): number {
  if (mode === "full") return FULL_WELCOME_DURATION_MS;
  if (mode === "short") return SHORT_WELCOME_DURATION_MS;
  return REDUCED_WELCOME_DURATION_MS;
}

function revealBanculemApp(): void {
  document.body.classList.remove("welcome-active");
  document.body.classList.remove("welcome-pending");
  window.dispatchEvent(new Event("banculem:ready"));
}

function removeWelcome(overlay: HTMLElement): void {
  if (!overlay.isConnected || overlay.classList.contains("is-leaving")) return;
  overlay.classList.add("is-leaving");
  window.setTimeout(() => {
    overlay.remove();
    if (!document.querySelector(".banculem-welcome")) revealBanculemApp();
  }, 280);
}

export function dismissBanculemLaunchCover(): void {
  document.querySelector<HTMLElement>(LAUNCH_COVER_SELECTOR)?.remove();
  revealBanculemApp();
}

export function mountBanculemWelcome(options: {
  greeting: string;
  storage?: WelcomeStorage | null;
  now?: Date;
  reduceMotion?: boolean;
  host?: HTMLElement;
}): WelcomeMode {
  const reduceMotion = options.reduceMotion
    ?? window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ?? false;
  let storage = options.storage;
  if (storage === undefined) {
    try { storage = window.localStorage; }
    catch { storage = null; }
  }
  const mode = resolveWelcomeMode(storage, options.now, reduceMotion);
  const host = options.host ?? document.body;
  const launchCover = host.querySelector<HTMLElement>(LAUNCH_COVER_SELECTOR);
  const overlay = launchCover ?? document.createElement("section");
  overlay.removeAttribute("id");
  overlay.className = `banculem-welcome banculem-welcome--${mode}`;
  overlay.dataset.banculemWelcome = mode;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "Bienvenida a Banculem");
  overlay.style.setProperty("--welcome-duration", `${welcomeDurationMs(mode)}ms`);
  overlay.innerHTML = `
    <button class="banculem-welcome-skip" type="button" aria-label="Omitir bienvenida">Omitir</button>
    <div class="banculem-welcome-glow" aria-hidden="true"></div>
    <div class="banculem-welcome-rings" aria-hidden="true"><i></i><i></i><i></i></div>
    <div class="banculem-welcome-mark" aria-hidden="true"><img src="/icons/banculem.svg" alt="" /></div>
    <div class="banculem-welcome-word" aria-hidden="true">Banculem</div>
    <div class="banculem-welcome-copy">
      <strong data-welcome-greeting></strong>
      <span>Todo en orden.</span>
    </div>`;
  const greeting = overlay.querySelector<HTMLElement>("[data-welcome-greeting]");
  if (greeting) greeting.textContent = options.greeting;

  document.body.classList.add("welcome-active");
  overlay.classList.add("is-playing");
  if (!overlay.isConnected) host.append(overlay);
  const skipButton = overlay.querySelector<HTMLButtonElement>(".banculem-welcome-skip");
  skipButton?.addEventListener("click", () => removeWelcome(overlay));
  overlay.addEventListener("keydown", (event) => { if (event.key === "Escape") removeWelcome(overlay); });
  overlay.addEventListener("animationend", (event) => { if (event.target === overlay) removeWelcome(overlay); });
  if (mode === "full") skipButton?.focus({ preventScroll: true });
  window.setTimeout(() => removeWelcome(overlay), welcomeDurationMs(mode));
  return mode;
}
